import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, BookMarked, FlaskConical, Feather, Users, ClipboardCheck, Heart, GraduationCap, MapPin, Phone, Sparkles, ShieldCheck, Star, ExternalLink } from 'lucide-react';
import AdmissionForm from '../components/AdmissionForm';
import { PROGRAMS, TESTIMONIALS, BRANCHES, HERO_IMAGE, GROUP_IMAGE, GOOGLE_REVIEW_URL } from '../data/mock';

const iconMap = { BookOpen, BookMarked, FlaskConical, Feather };

const HERO_STATS = [
  { v: '60+', l: 'Years of legacy' },
  { v: '2', l: 'Branches in WB' },
  { v: 'K–12', l: 'ICSE·CBSE·ISC' },
];

const WHY_FEATURES = [
  { icon: GraduationCap, title: 'Experienced Faculty', desc: 'Dedicated teachers with decades of board-exam expertise.' },
  { icon: Users, title: 'Small Batch Sizes', desc: 'Individual attention so every student is heard and supported.' },
  { icon: ClipboardCheck, title: 'Regular Assessments', desc: 'Periodic tests and progress reports shared with parents.' },
  { icon: Heart, title: 'Long-term Mentoring', desc: 'Academic mentoring that builds discipline & study habits.' },
];

const TRUST_STATS = [
  { v: '60+ yrs', l: 'Trusted legacy' },
  { v: '1000s', l: 'Students mentored' },
  { v: 'ICSE/CBSE', l: 'Board aligned' },
  { v: 'K–12', l: 'Class 5 to 12' },
];

function FiveStars({ size = 'w-4 h-4' }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map((i) => <Star key={i} className={`${size} fill-amber-400 text-amber-400`} />)}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
      <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
      <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"/>
      <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
    </svg>
  );
}

function HeroSection() {
  return (
    <section className="relative overflow-hidden coral-gradient">
      <div className="absolute inset-0 dotted-bg opacity-60" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="text-white">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur border border-white/25 rounded-full px-4 py-1.5 text-[0.8rem] font-medium">
            <Sparkles className="w-3.5 h-3.5" /> Admissions Open · 2026–27 Session
          </div>
          <h1 className="font-serif font-semibold text-5xl md:text-6xl lg:text-[4.2rem] leading-[1.05] mt-6 tracking-tight">
            60 Years of Academic <span className="gold-text italic">Excellence in Kolkata</span>
          </h1>
          <p className="text-white/90 mt-6 text-lg leading-relaxed max-w-xl">
            RN Academy is one of the most trusted K-12 coaching institutions in Behala, Kolkata and Howrah, West Bengal. Structured, board-aligned education for students from Class 5 to Class 12 — ICSE, CBSE & ISC.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/admissions" className="inline-flex items-center gap-2 bg-white text-coral hover:bg-amber-50 smooth font-semibold px-6 py-3.5 rounded-xl shadow-coral">
              Apply for Admission <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/programs" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/30 text-white smooth font-medium px-6 py-3.5 rounded-xl">
              Explore Programs
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-12 max-w-md">
            {HERO_STATS.map((s) => (
              <div key={s.l}>
                <div className="font-serif text-3xl md:text-4xl font-semibold gold-text">{s.v}</div>
                <div className="text-white/80 text-xs mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white/20">
            <img src={HERO_IMAGE} alt="RN Academy students" className="w-full h-[520px] object-cover" />
            <div className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-full px-4 py-1.5 text-xs font-semibold text-coral flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Trusted Since 1965
            </div>
            <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur rounded-full px-4 py-1.5 text-xs font-semibold text-coral flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" /> Behala · Howrah
            </div>
          </div>
          <div className="absolute -bottom-6 -left-6 bg-amber-300 text-stone-900 rounded-2xl px-5 py-3 shadow-2xl rotate-[-4deg]">
            <div className="text-xs font-semibold uppercase tracking-wider">Enroll Now</div>
            <div className="font-serif font-bold text-lg">2026–27</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function QuickFormSection() {
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <div className="text-coral text-xs font-semibold uppercase tracking-[0.18em]">Apply in under 60 seconds.</div>
        <h2 className="font-serif text-4xl md:text-5xl font-semibold mt-3 leading-tight">
          Submit basic details and our admissions team will <span className="text-coral">call you within 24 hours</span> to guide you through the next steps.
        </h2>
        <ul className="mt-7 space-y-3 text-stone-700">
          {['Free academic counselling', 'Small batch sizes & individual attention', 'Branches in Behala & Howrah'].map((t) => (
            <li key={t} className="flex items-center gap-3">
              <span className="w-7 h-7 rounded-full bg-coral-light text-coral flex items-center justify-center text-sm">✓</span>
              {t}
            </li>
          ))}
        </ul>
      </div>
      <AdmissionForm />
    </section>
  );
}

function WhyChooseSection() {
  return (
    <section className="bg-[#fff1ea]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="max-w-3xl">
          <div className="text-coral text-xs font-semibold uppercase tracking-[0.18em]">Why Choose Us</div>
          <h2 className="font-serif text-4xl md:text-5xl font-semibold mt-3 leading-tight">
            A benchmark of disciplined, <span className="italic text-amber-600">results-driven preparation.</span>
          </h2>
          <p className="text-stone-600 mt-4 text-lg">We focus on academic mentoring for long-term success — not just exam scores.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {WHY_FEATURES.map((f) => (
            <div key={f.title} className="bg-white rounded-2xl p-7 border border-orange-100 card-hover smooth">
              <div className="w-12 h-12 rounded-xl bg-coral-light text-coral flex items-center justify-center mb-5">
                <f.icon className="w-6 h-6" />
              </div>
              <h3 className="font-serif font-semibold text-xl mb-2">{f.title}</h3>
              <p className="text-stone-600 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProgramsSection() {
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="max-w-2xl">
          <div className="text-coral text-xs font-semibold uppercase tracking-[0.18em]">Our Programs</div>
          <h2 className="font-serif text-4xl md:text-5xl font-semibold mt-3 leading-tight">
            Structured learning from <span className="italic text-amber-600">Class 5 to 12.</span>
          </h2>
        </div>
        <Link to="/programs" className="inline-flex items-center gap-2 text-coral font-medium hover:gap-3 smooth">
          View all programs <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
        {PROGRAMS.map((p) => {
          const Icon = iconMap[p.icon];
          return (
            <div key={p.title} className="bg-white rounded-2xl p-6 border border-orange-100 card-hover smooth flex flex-col">
              <div className="w-12 h-12 rounded-xl bg-coral-light text-coral flex items-center justify-center mb-4">
                <Icon className="w-6 h-6" />
              </div>
              <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{p.badge}</div>
              <h3 className="font-serif font-semibold text-xl mt-1">{p.title}</h3>
              {p.tag && <div className="text-xs text-amber-700 bg-amber-100 inline-block px-2 py-0.5 rounded-full mt-2 w-fit font-medium">{p.tag}</div>}
              <p className="text-stone-600 text-sm mt-3 leading-relaxed flex-1">{p.desc}</p>
              <Link to="/programs" className="text-coral font-medium text-sm mt-4 inline-flex items-center gap-1 hover:gap-2 smooth">
                Learn more <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function TestimonialsSection() {
  return (
    <section className="bg-[#fff1ea]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white border border-orange-200 rounded-full px-4 py-1.5 text-xs font-semibold text-stone-700 shadow-sm">
            <FiveStars size="w-3.5 h-3.5" />
            <span>Loved on Google</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-semibold mt-5 leading-tight">In the words of <span className="italic text-amber-600">our students.</span></h2>
          <p className="text-stone-600 mt-4 text-lg">Real reviews from RN Academy students and alumni, shared on Google.</p>
        </div>

        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mt-10 max-w-5xl mx-auto">
          {TRUST_STATS.map((s) => (
            <div key={s.l} className="text-center bg-white rounded-2xl p-6 border border-orange-100">
              <div className="font-serif text-3xl font-semibold text-coral">{s.v}</div>
              <div className="text-stone-600 text-sm mt-1">{s.l}</div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="bg-white rounded-2xl p-7 border border-orange-100 smooth card-hover flex flex-col">
              <div className="flex items-center justify-between">
                <FiveStars />
                <GoogleIcon />
              </div>
              <p className="text-stone-700 mt-4 leading-relaxed flex-1">{t.quote}</p>
              <div className="mt-5 pt-4 border-t border-orange-100">
                <div className="font-semibold">{t.name}</div>
                <div className="text-stone-500 text-xs mt-0.5">{t.sub}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mt-10">
          <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noreferrer" className="bg-white border border-orange-200 hover:bg-coral-light smooth font-medium px-6 py-3 rounded-xl inline-flex items-center gap-2 text-stone-800">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" /> See all reviews on Google <ExternalLink className="w-3.5 h-3.5 opacity-60" />
          </a>
          <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noreferrer" className="btn-primary smooth font-medium px-6 py-3 rounded-xl inline-flex items-center gap-2">
            Write a review <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

function BranchesSection() {
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <div className="text-coral text-xs font-semibold uppercase tracking-[0.18em]">Our Branches</div>
        <h2 className="font-serif text-4xl md:text-5xl font-semibold mt-3 leading-tight">Two convenient <span className="italic text-amber-600">locations.</span></h2>
        <div className="grid sm:grid-cols-2 gap-5 mt-10">
          {BRANCHES.map((b) => (
            <div key={b.name} className="bg-white rounded-2xl p-6 border border-orange-100 smooth card-hover">
              <div className="w-11 h-11 rounded-xl bg-coral-light text-coral flex items-center justify-center mb-4">
                <MapPin className="w-5 h-5" />
              </div>
              <h3 className="font-serif font-semibold text-xl">{b.name}</h3>
              <p className="text-stone-600 text-sm mt-2 leading-relaxed">{b.address}</p>
              <a href={`tel:${b.tel}`} className="inline-flex items-center gap-2 text-coral font-medium text-sm mt-4 hover:gap-3 smooth">
                <Phone className="w-4 h-4" /> {b.phone}
              </a>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-3xl overflow-hidden shadow-2xl">
        <img src={GROUP_IMAGE} alt="RN Academy students" className="w-full h-[520px] object-cover" />
      </div>
    </section>
  );
}

function CtaSection() {
  return (
    <section className="px-5 sm:px-8 pb-24">
      <div className="max-w-7xl mx-auto coral-gradient rounded-3xl px-8 py-16 text-center text-white relative overflow-hidden">
        <div className="absolute inset-0 dotted-bg opacity-50" />
        <div className="relative">
          <h2 className="font-serif text-4xl md:text-5xl font-semibold leading-tight">Enroll Now for the <span className="gold-text italic">2026–27 Session</span></h2>
          <p className="text-white/90 mt-4 text-lg">Guiding students towards academic excellence.</p>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <Link to="/admissions" className="bg-white text-coral hover:bg-amber-50 smooth font-semibold px-6 py-3.5 rounded-xl shadow-lg">Apply for Admission</Link>
            <Link to="/admissions" className="bg-white/10 hover:bg-white/20 backdrop-blur border border-white/40 text-white smooth font-medium px-6 py-3.5 rounded-xl">Book Campus Visit</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div className="fade-in">
      <HeroSection />
      <QuickFormSection />
      <WhyChooseSection />
      <ProgramsSection />
      <TestimonialsSection />
      <BranchesSection />
      <CtaSection />
    </div>
  );
}
